<template>
  <lf-modal large :close="closeModal">
    <template #header>
      <div class="flex space-x-2">
        <lf-h1>{{ title }}</lf-h1>
        <lf-badge v-if="offerTemplateName">{{ offerTemplateName }}</lf-badge>
        <lf-badge v-if="funderName">{{ funderName }}</lf-badge>
      </div>
    </template>
    <template #content>
      <form data-cy="wfb-offer-stips-modal" @submit="onSubmit">
        <stips-component
          ref="stipsList"
          class="px-6 pb-6"
          disable-edit
          hide-title
          :select-only="isSelectOnly"
          :required-stips="stips"
        />
        <div class="flex justify-end border-t border-gray-200 p-6">
          <modal-buttons
            save-is-submit
            :disabled="isSubmitting"
            :on-cancel="closeModal"
          />
        </div>
      </form>
    </template>
  </lf-modal>
</template>
<script setup lang="ts">
import { computed, useTemplateRef } from "vue";

import union from "lodash/union";
import { useForm } from "vee-validate";

import LfModal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import StipsComponent from "@/views/deals/components/Stips.vue";

import type { IFunder } from "@/models/funders";
import type { OfferTemplate } from "@/models/offers";
import type { StipDefinition } from "@/models/stips";

const {
  stips,
  title = "",
  offerTemplate,
  funder,
  isSelectOnly = false,
} = defineProps<{
  stips: StipDefinition[];
  title: string;
  offerTemplate: OfferTemplate;
  funder?: IFunder;
  isSelectOnly?: boolean;
}>();

const { handleSubmit, isSubmitting } = useForm({
  initialValues: {
    stips: stips,
  },
});

const emit = defineEmits<{
  close: [void];
  stipsUpdated: [number[]];
}>();

const stipsList =
  useTemplateRef<InstanceType<typeof StipsComponent>>("stipsList");

const funderName = computed(() => funder?.full_name ?? "");

const offerTemplateName = computed(() => offerTemplate?.name ?? "");

const closeModal = () => {
  emit("close");
};

const onSubmit = handleSubmit(async (values) => {
  let newStipIds = values.stips.map((stip) => stip.id);
  if (stipsList.value) {
    const withCreatedStip = await stipsList.value.externalOnSubmit();
    newStipIds = union(
      newStipIds,
      withCreatedStip.map((stip) => stip.id)
    );
  }
  emit("stipsUpdated", newStipIds);
  closeModal();
});
</script>
